import React, { useMemo } from "react";
import { Helmet } from "react-helmet-async";

const BASE_URL = "https://fincombank.com";
const APP_PATH = "/fincompay";
const OG_IMAGE_URL = `${BASE_URL}/assets/images/og-fincompay-1200x630.jpg`;
const LOGO_URL = `${BASE_URL}/assets/images/logo.png`;

type SeoLang = "ro" | "ru" | "en";

const seoData: Record<
  SeoLang,
  { title: string; desc: string; keywords: string }
> = {
  ro: {
    title: "FinComPay Personal & Business - Internet Banking | FinComBank",
    desc: "Conectează-te la FinComPay. Internet banking pentru persoane fizice și afaceri. Gestionează conturi, carduri, credite și accesează FinComPay Business online 24/7.",
    keywords:
      "fincompay, fincompay business, fincombank online, internet banking pentru afaceri, internet banking moldova, autentificare banca, cont business, afaceri de la fincombank, aplicatie mobila fincombank",
  },
  ru: {
    title: "FinComPay Личный и Бизнес - Интернет-банкинг | FinComBank",
    desc: "Войдите в FinComPay. Интернет-банкинг для физических лиц и бизнеса. Управляйте счетами, картами, кредитами и FinComPay Business онлайн 24/7.",
    keywords:
      "fincompay, fincompay business, fincombank онлайн, интернет-банкинг для бизнеса, интернет банкинг молдова, вход в банк, бизнес счет, бизнес fincombank, мобильное приложение fincombank",
  },
  en: {
    title: "FinComPay Personal & Business - Internet Banking | FinComBank",
    desc: "Log in to FinComPay. Internet banking for individuals and businesses. Manage your accounts, cards, loans, and access FinComPay Business online 24/7.",
    keywords:
      "fincompay, fincompay business, fincombank online, business internet banking, internet banking moldova, bank login, business account, fincombank business, mobile app",
  },
};

const orgDescription: Record<SeoLang, string> = {
  ro: "Banca ta, oriunde ai fi. Oferim soluții financiare pentru persoane fizice și afaceri.",
  ru: "Ваш банк, где бы вы ни были. Финансовые решения для частных лиц и бизнеса.",
  en: "Your bank, wherever you are. Financial solutions for individuals and businesses.",
};

const businessProductDesc: Record<SeoLang, string> = {
  ro: "Platformă de internet banking dedicată pentru afaceri, antreprenori și companii de la FinComBank.",
  ru: "Платформа интернет-банкинга для бизнеса, предпринимателей и компаний FinComBank.",
  en: "Internet banking platform for businesses, entrepreneurs and companies from FinComBank.",
};

const featureLists: Record<SeoLang, string[]> = {
  ro: [
    "Internet Banking Personal",
    "FinComPay Business (Internet Banking pentru Afaceri)",
    "Management Conturi și Carduri Corporative",
    "Credite și Depozite",
    "Plăți miaPOS",
  ],
  ru: [
    "Интернет-банкинг для физических лиц",
    "FinComPay Business (интернет-банкинг для бизнеса)",
    "Управление счетами и корпоративными картами",
    "Кредиты и депозиты",
    "Платежи miaPOS",
  ],
  en: [
    "Personal Internet Banking",
    "FinComPay Business (business internet banking)",
    "Corporate accounts and cards management",
    "Loans and deposits",
    "miaPOS payments",
  ],
};

function normalizeLang(lang: string): SeoLang {
  const base = (lang || "ro").split("-")[0].toLowerCase();
  if (base === "ru") return "ru";
  if (base === "en") return "en";
  return "ro";
}

type HomeSeoHeadProps = {
  lang: string;
};

const HomeSeoHead: React.FC<HomeSeoHeadProps> = ({ lang }) => {
  const seoLang = normalizeLang(lang);
  const currentSeo = seoData[seoLang];

  const currentUrl = useMemo(
    () => `${BASE_URL}/${seoLang === "en" ? "en" : seoLang}${APP_PATH}`,
    [seoLang],
  );

  const jsonLdGraph = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${BASE_URL}/#website`,
          url: BASE_URL,
          name: "FinComBank",
          publisher: { "@id": `${BASE_URL}/#organization` },
        },
        {
          "@type": "BankOrCreditUnion",
          "@id": `${BASE_URL}/#organization`,
          name: "FinComBank S.A.",
          url: BASE_URL,
          logo: LOGO_URL,
          image: LOGO_URL,
          description: orgDescription[seoLang],
          telephone: "+373 22-26-99-99",
          email: "callcenter@fincombank.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "str. A. Pușkin 26",
            addressLocality: "Chișinău",
            postalCode: "MD-2012",
            addressCountry: "MD",
          },
          sameAs: [
            "https://www.facebook.com/fincombank",
            "https://www.instagram.com/fincombank",
            "https://www.linkedin.com/company/fincombank-s-a-",
          ],
        },
        {
          "@type": "FinancialProduct",
          "@id": `${BASE_URL}/#business-banking`,
          name: "FinComPay Business",
          description: businessProductDesc[seoLang],
          provider: {
            "@id": `${BASE_URL}/#organization`,
          },
        },
        {
          "@type": "SoftwareApplication",
          "@id": `${currentUrl}/#app`,
          name: "FinComPay",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web, iOS, Android",
          url: currentUrl,
          provider: {
            "@id": `${BASE_URL}/#organization`,
          },
          description: currentSeo.desc,
          featureList: featureLists[seoLang],
        },
        {
          "@type": "WebPage",
          "@id": `${currentUrl}/#webpage`,
          url: currentUrl,
          name: currentSeo.title,
          description: currentSeo.desc,
          inLanguage: seoLang,
          isPartOf: {
            "@id": `${BASE_URL}/#website`,
          },
          about: [
            { "@id": `${currentUrl}/#app` },
            { "@id": `${BASE_URL}/#business-banking` },
          ],
        },
      ],
    }),
    [currentSeo, currentUrl, seoLang],
  );

  const ogLocale =
    seoLang === "ro" ? "ro_MD" : seoLang === "ru" ? "ru_RU" : "en_US";

  return (
    <Helmet>
      <html lang={seoLang} />
      <title>{currentSeo.title}</title>
      <meta name="description" content={currentSeo.desc} />
      <meta name="keywords" content={currentSeo.keywords} />
      <meta
        name="robots"
        content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
      />

      <link rel="canonical" href={currentUrl} />

      <link
        rel="alternate"
        hrefLang="ro-MD"
        href={`${BASE_URL}/ro${APP_PATH}`}
      />
      <link
        rel="alternate"
        hrefLang="ru-MD"
        href={`${BASE_URL}/ru${APP_PATH}`}
      />
      <link
        rel="alternate"
        hrefLang="en-MD"
        href={`${BASE_URL}/en${APP_PATH}`}
      />
      <link
        rel="alternate"
        hrefLang="x-default"
        href={`${BASE_URL}/ro${APP_PATH}`}
      />

      <meta property="og:site_name" content="FinComBank" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={currentSeo.title} />
      <meta property="og:description" content={currentSeo.desc} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={OG_IMAGE_URL} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content="ro_MD" />
      <meta property="og:locale:alternate" content="ru_RU" />
      <meta property="og:locale:alternate" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={currentSeo.title} />
      <meta name="twitter:description" content={currentSeo.desc} />
      <meta name="twitter:image" content={OG_IMAGE_URL} />

      <script type="application/ld+json">{JSON.stringify(jsonLdGraph)}</script>
    </Helmet>
  );
};

export default HomeSeoHead;
